export type ConcurrencyLimiter = <T>(task: () => Promise<T>, signal?: AbortSignal) => Promise<T>;

/**
 * Runs at most `max` tasks at once; the rest wait in arrival order. A waiting task whose `signal`
 * aborts is dropped without being started.
 */
export function createConcurrencyLimiter(max: number): ConcurrencyLimiter {
  if (!Number.isInteger(max) || max < 1) {
    throw new RangeError(`max must be a positive integer, got ${max}`);
  }

  let running = 0;
  const waiting: (() => void)[] = [];

  const release = () => {
    running--;
    waiting.shift()?.();
  };

  return <T>(task: () => Promise<T>, signal?: AbortSignal) =>
    new Promise<T>((resolve, reject) => {
      const start = () => {
        signal?.removeEventListener("abort", onAbort);
        running++;
        task().then(resolve, reject).finally(release);
      };
      const onAbort = () => {
        const index = waiting.indexOf(start);
        if (index >= 0) waiting.splice(index, 1);
        reject(signal?.reason);
      };

      if (signal?.aborted) {
        reject(signal.reason);
        return;
      }
      if (running < max) {
        start();
        return;
      }
      signal?.addEventListener("abort", onAbort, { once: true });
      waiting.push(start);
    });
}
